import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import {
  Bullseye,
  Button,
  Card,
  CardBody,
  CardTitle,
  Content,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  EmptyStateVariant,
  Flex,
  FlexItem,
  Form,
  FormGroup,
  FormHelperText,
  HelperText,
  HelperTextItem,
  Spinner,
  Switch,
  ToggleGroup,
  ToggleGroupItem,
} from '@patternfly/react-core';
import { DesktopIcon, ExclamationTriangleIcon, MoonIcon, RedoIcon, SunIcon } from '@patternfly/react-icons';
import { useApiFetch } from '../../api';
import { useNotifications } from '../../contexts/NotificationContext';
import { useTheme } from '../../contexts/theme/ThemeContext';
import { formatDateTime } from '../../utils/dateFormat';
import { EmailLanguagePreference, ThemePreference, UserPreferencesUpdate } from '../../types/models';
import { ApiResponseUserPreferences } from '../../types/api/ApiResponseUserPreferences';

const THEME_OPTIONS: { value: ThemePreference; label: string; icon: React.ReactNode }[] = [
  { value: 'system', label: 'System', icon: <DesktopIcon /> },
  { value: 'light', label: 'Light', icon: <SunIcon /> },
  { value: 'dark', label: 'Dark', icon: <MoonIcon /> },
];

const themeLabel = (value: ThemePreference): string =>
  THEME_OPTIONS.find((option) => option.value === value)?.label ?? value;

// Each label is written in its own language, so a user can find theirs whatever they read.
const EMAIL_LANGUAGE_OPTIONS: { value: EmailLanguagePreference; label: string }[] = [
  { value: 'fr', label: 'Français' },
  { value: 'en', label: 'English' },
];

const emailLanguageLabel = (value: EmailLanguagePreference): string =>
  EMAIL_LANGUAGE_OPTIONS.find((option) => option.value === value)?.label ?? value;

/**
 * Preferences tab of the User Profile page.
 *
 * Reads/writes the authenticated user's own preferences via
 * `GET`/`PUT /users/me/preferences` (self-scoped, so it works even under
 * impersonation and for clientless pre-onboarding users). Each control
 * auto-saves on change (optimistic update, reverted if the request fails).
 *
 * The endpoints wrap responses in JSend and map failures to `status: 'error'`
 * at HTTP 200, so every branch checks `body.status === 'success'` — never
 * `response.ok` (see CLAUDE.md "Check JSend body.status").
 */
function PreferencesForm() {
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();
  const { themePreference, setThemePreference } = useTheme();

  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Seed the theme selection from the app-wide cache so it reflects the applied
  // theme immediately; the GET below reconciles it against the server.
  const [theme, setTheme] = useState<ThemePreference>(themePreference);
  const [newsletter, setNewsletter] = useState<boolean>(false);
  // `null` while the backend predates `email_language` — the control stays hidden
  // then, since the PUT rejects unknown keys and every save would fail.
  const [emailLanguage, setEmailLanguage] = useState<EmailLanguagePreference | null>(null);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);

  const [savingTheme, setSavingTheme] = useState<boolean>(false);
  const [savingNewsletter, setSavingNewsletter] = useState<boolean>(false);
  const [savingEmailLanguage, setSavingEmailLanguage] = useState<boolean>(false);

  const fetchPreferences = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const response = await apiFetch('/users/me/preferences');
      const body = (await response.json().catch(() => null)) as ApiResponseUserPreferences | null;

      if (body && body.status === 'success' && body.data?.preferences) {
        setTheme(body.data.preferences.theme);
        setNewsletter(body.data.preferences.newsletter_opt_in);
        setEmailLanguage(body.data.preferences.email_language ?? null);
        setUpdatedAt(body.data.updated_at ?? null);
        // Server is the source of truth — reconcile the app-wide theme cache so
        // the applied theme matches what's saved (e.g. changed on another device).
        setThemePreference(body.data.preferences.theme);
      } else {
        setLoadError(body?.message ?? 'Failed to load your preferences.');
      }
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'An unexpected error occurred.');
    } finally {
      setLoading(false);
    }
  }, [apiFetch, setThemePreference]);

  useEffect(() => {
    void fetchPreferences();
  }, [fetchPreferences]);

  const persist = useCallback(
    async (patch: UserPreferencesUpdate): Promise<ApiResponseUserPreferences> => {
      const response = await apiFetch('/users/me/preferences', {
        method: 'PUT',
        headers: { Accept: 'application/json' },
        body: JSON.stringify(patch),
      });
      const body = (await response.json().catch(() => null)) as ApiResponseUserPreferences | null;
      if (!body || body.status !== 'success' || !body.data?.preferences) {
        throw new Error(body?.message ?? 'Failed to update your preferences.');
      }
      return body;
    },
    [apiFetch],
  );

  // Success feedback is intentionally recorded WITHOUT opening the drawer: these
  // controls auto-save on every change, so popping the drawer on each toggle would
  // be noisy. The control's own state change is the immediate confirmation, and the
  // notification is kept in the bell/history. Failures (below) DO open the drawer.
  const notifySuccess = useCallback(
    (description: string) => {
      addNotification({ title: 'Preferences updated', srTitle: 'Success alert', description, variant: 'success' });
    },
    [addNotification],
  );

  const notifyError = useCallback(
    (err: unknown) => {
      addNotification({
        title: 'Preferences update failed',
        srTitle: 'Error alert',
        description: err instanceof Error ? err.message : 'An unexpected error occurred.',
        variant: 'danger',
      });
      setDrawerOpen(true);
    },
    [addNotification, setDrawerOpen],
  );

  const handleThemeChange = useCallback(
    async (next: ThemePreference) => {
      if (next === theme || savingTheme) {
        return;
      }
      const previousTheme = theme;
      const previousPreference = themePreference;
      setTheme(next);
      setThemePreference(next); // apply live + cache
      setSavingTheme(true);
      try {
        const body = await persist({ theme: next });
        setUpdatedAt(body.data.updated_at ?? null);
        setTheme(body.data.preferences.theme);
        setThemePreference(body.data.preferences.theme);
        notifySuccess(`Theme set to ${themeLabel(body.data.preferences.theme)}.`);
      } catch (err) {
        setTheme(previousTheme);
        setThemePreference(previousPreference);
        notifyError(err);
      } finally {
        setSavingTheme(false);
      }
    },
    [theme, savingTheme, themePreference, setThemePreference, persist, notifySuccess, notifyError],
  );

  const handleNewsletterChange = useCallback(
    async (checked: boolean) => {
      if (savingNewsletter) {
        return;
      }
      const previous = newsletter;
      setNewsletter(checked);
      setSavingNewsletter(true);
      try {
        const body = await persist({ newsletter_opt_in: checked });
        setUpdatedAt(body.data.updated_at ?? null);
        setNewsletter(body.data.preferences.newsletter_opt_in);
        notifySuccess(`Newsletter subscription ${body.data.preferences.newsletter_opt_in ? 'enabled' : 'disabled'}.`);
      } catch (err) {
        setNewsletter(previous);
        notifyError(err);
      } finally {
        setSavingNewsletter(false);
      }
    },
    [savingNewsletter, newsletter, persist, notifySuccess, notifyError],
  );

  const handleEmailLanguageChange = useCallback(
    async (next: EmailLanguagePreference) => {
      if (next === emailLanguage || savingEmailLanguage) {
        return;
      }
      const previous = emailLanguage;
      setEmailLanguage(next);
      setSavingEmailLanguage(true);
      try {
        const body = await persist({ email_language: next });
        setUpdatedAt(body.data.updated_at ?? null);
        setEmailLanguage(body.data.preferences.email_language);
        notifySuccess(`Email language set to ${emailLanguageLabel(body.data.preferences.email_language)}.`);
      } catch (err) {
        setEmailLanguage(previous);
        notifyError(err);
      } finally {
        setSavingEmailLanguage(false);
      }
    },
    [emailLanguage, savingEmailLanguage, persist, notifySuccess, notifyError],
  );

  if (loading) {
    return (
      <Bullseye>
        <Spinner aria-label="Loading preferences" />
      </Bullseye>
    );
  }

  if (loadError) {
    return (
      <EmptyState
        variant={EmptyStateVariant.sm}
        status="danger"
        headingLevel="h2"
        icon={ExclamationTriangleIcon}
        titleText="Unable to load preferences"
      >
        <EmptyStateBody>{loadError}</EmptyStateBody>
        <EmptyStateFooter>
          <EmptyStateActions>
            <Button variant="primary" icon={<RedoIcon />} onClick={() => void fetchPreferences()}>
              Try again
            </Button>
          </EmptyStateActions>
        </EmptyStateFooter>
      </EmptyState>
    );
  }

  return (
    <Card isPlain>
      <CardTitle>Preferences</CardTitle>
      <CardBody>
        <Form>
          <FormGroup label="Theme" fieldId="theme-preference" role="group">
            <Flex alignItems={{ default: 'alignItemsCenter' }} spaceItems={{ default: 'spaceItemsMd' }}>
              <FlexItem>
                <ToggleGroup aria-label="Theme preference">
                  {THEME_OPTIONS.map((option) => (
                    <ToggleGroupItem
                      key={option.value}
                      icon={option.icon}
                      text={option.label}
                      buttonId={`theme-${option.value}`}
                      isSelected={theme === option.value}
                      isDisabled={savingTheme}
                      onChange={() => void handleThemeChange(option.value)}
                    />
                  ))}
                </ToggleGroup>
              </FlexItem>
              {savingTheme && (
                <FlexItem>
                  <Spinner size="md" aria-label="Saving theme preference" />
                </FlexItem>
              )}
            </Flex>
            <FormHelperText>
              <HelperText>
                <HelperTextItem>
                  Choose how the interface looks. “System” follows your device’s light/dark setting.
                </HelperTextItem>
              </HelperText>
            </FormHelperText>
          </FormGroup>

          {emailLanguage !== null && (
            <FormGroup label="Email language" fieldId="email-language-preference" role="group">
              <Flex alignItems={{ default: 'alignItemsCenter' }} spaceItems={{ default: 'spaceItemsMd' }}>
                <FlexItem>
                  <ToggleGroup aria-label="Email language preference">
                    {EMAIL_LANGUAGE_OPTIONS.map((option) => (
                      <ToggleGroupItem
                        key={option.value}
                        text={<span lang={option.value}>{option.label}</span>}
                        buttonId={`email-language-${option.value}`}
                        isSelected={emailLanguage === option.value}
                        isDisabled={savingEmailLanguage}
                        onChange={() => void handleEmailLanguageChange(option.value)}
                      />
                    ))}
                  </ToggleGroup>
                </FlexItem>
                {savingEmailLanguage && (
                  <FlexItem>
                    <Spinner size="md" aria-label="Saving email language preference" />
                  </FlexItem>
                )}
              </Flex>
              <FormHelperText>
                <HelperText>
                  <HelperTextItem>
                    The emails Manager sends you are written in this language only: domain renewal notices, wallet
                    top-up receipts, and mailbox credentials sent to your address. Invitations you send use it too.
                  </HelperTextItem>
                </HelperText>
              </FormHelperText>
            </FormGroup>
          )}

          <FormGroup label="Newsletter" fieldId="newsletter-switch">
            <Flex alignItems={{ default: 'alignItemsCenter' }} spaceItems={{ default: 'spaceItemsMd' }}>
              <FlexItem>
                <Switch
                  id="newsletter-switch"
                  label="Subscribe to the newsletter"
                  isChecked={newsletter}
                  isDisabled={savingNewsletter}
                  onChange={(_event, checked) => void handleNewsletterChange(checked)}
                />
              </FlexItem>
              {savingNewsletter && (
                <FlexItem>
                  <Spinner size="md" aria-label="Saving newsletter preference" />
                </FlexItem>
              )}
            </Flex>
            <FormHelperText>
              <HelperText>
                <HelperTextItem>Occasional product news and updates. You can opt out at any time.</HelperTextItem>
              </HelperText>
            </FormHelperText>
          </FormGroup>
        </Form>

        <div className="pf-v6-u-mt-md">
          <Content component="small">
            {updatedAt ? `Last updated ${formatDateTime(updatedAt)}` : 'Using default preferences — nothing saved yet.'}
          </Content>
        </div>
      </CardBody>
    </Card>
  );
}

export default PreferencesForm;
